import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import i18next from 'i18next';
import { translate } from 'react-i18next';

import Countdown_en from './locales/Countdown_en.json';
import Countdown_fr from './locales/Countdown_fr.json';

class Countdown extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      time: null,
      state: null,
      pausedAt: null
    };

    this.countdown = null;

    i18next.addResourceBundle('en', 'Countdown', Countdown_en);
    i18next.addResourceBundle('fr', 'Countdown', Countdown_fr);
  }

  componentDidMount() {
    if (this.props.autoStart)
      this.setState({
        state: 'start'
      });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.state !== this.props.state &&
      this.props.state !== this.state.state
    ) {
      this.setState({
        state: this.props.state
      });
    }

    if (prevState.state !== this.state.state) {
      switch (this.state.state) {
        case 'start':
          this.start();
          break;
        case 'stop':
          this.stop();
          break;
        case 'pause':
          this.pause();
          break;
        case 'resume':
          this.resume();
          break;
        case 'reset':
          this.reset();
          break;
      }
    }
  }

  // Set Interval
  timer = (end, interval) => setInterval(() => {
      const now = moment();
      let remaining = end.diff(now);

      remaining = remaining > 0
        ? remaining
        : 0;

      this.setState({
        time: moment.duration(remaining)
      });

      if (!remaining)
        this.stop();
    }, interval);

  // Clear Timer
  clearTimer = () => {
    if (this.countdown) {
      clearInterval(this.countdown);
      this.countdown = null;
    }
  };

  // Start Countdown
  start = () => {
    const { duration, eventTime, interval } = this.props;

    this.endTime = eventTime // Ends on set event time
        ? eventTime
        : this.state.pausedAt // Ends on remaining time
          ? moment().add(this.state.pausedAt.asMilliseconds(), 'ms')
          : duration // Ends on initial duration
            ? moment().add(duration, 'ms')
            : null;

    if (this.pausedAt) {
      this.setState({
        pausedAt: null
      });
    }

    this.clearTimer();
    this.countdown = this.timer(this.endTime, interval);
  };

  // Stop Countdown
  stop = () => {
    this.clearTimer();
    this.setState({
      pausedAt: null,
      state: 'stop'
    });
  };

  // Pause Countdown
  pause = () => {
    this.clearTimer();
    this.setState({
      pausedAt: this.state.time
    });
  };

  // Resume Countdown (restart countdow from pausedAt)
  resume = () => {
    this.clearTimer();
    this.setState({
      state: 'start'
    });
  };

  // Reset timer and restart it
  reset = (restart = true) => {
    this.clearTimer();
    this.setState({
      pausedAt: null,
      ...(restart
        ? { state: 'start' }
        : {}
      )
    });
  };

  // Display Time
  display = time => {
    const { t, display } = this.props;
    const
      d = parseInt(time.asHours() / 24),
      h = display.days && !display.short
        ? time.hours()
        : parseInt(time.asHours()),
      m = display.hours
        ? time.minutes()
        : parseInt(time.asMinutes()),
      s = display.minutes
        ? time.seconds()
        : parseInt(time.asSeconds()),
      ms = display.seconds
        ? time.milliseconds()
        : parseInt(time.asMilliseconds());
    let text = '';

    // Display short format
    if (display.short) {
      if (display.hours)
        text += this.pad(h);

      if (display.hours && display.minutes)
        text += ':';

      if (display.minutes)
        text += this.pad(m);

      if (display.minutes && display.seconds)
        text += ':';

      if (display.seconds)
        text += this.pad(s);

      if (display.seconds && display.milliseconds)
        text += '.';

      if (display.milliseconds)
        text += this.pad(ms, 3);
    }
    // Display long format
    else {
      if (
        display.days && !display.hours ||
        display.days && display.hours && d > 0
      )
        text += t('d', { count: d });

      if (
        display.hours && !display.minutes ||
        display.hours && display.minutes && h > 0
      )
        text += t('h', { count: h });

      if (
        display.minutes && !display.seconds ||
        display.minutes && display.seconds && m > 0
      )
        text += t('m', { count: m });

      if (
        display.seconds && !display.milliseconds ||
        display.seconds && display.milliseconds && s > 0
      )
        text += t('s', { count: s });

      if (display.milliseconds)
        text += t('ms', { count: ms });
    }

    return text.trim();
  }

  // Pad number with 0
  pad = (content, length = 2) => {
    let text = content.toString();

    while (text.length < length) {
      text = '0' + text;
    }

    return text;
  }

  // Call action on time complete
  handleFinish = () => {
    this.props.onFinish();
  };

  render() {
    const { format } = this.props;
    const { time } = this.state;

    return (
      <div className="Countdown">
        {time && this.display(time)}
      </div>
    );
  }
}

Countdown.propTypes = {
  duration: PropTypes.number, // milliseconds (required without enventTime)
  eventTime: PropTypes.object, // moment object (required without duration)
  onFinish: PropTypes.func,
  autoStart: PropTypes.bool,
  state: PropTypes.string,
  display: PropTypes.object,
};

Countdown.defaultProps = {
  duration: 0,
  eventTime: null,
  onFinish: () => {},
  autoStart: true,
  state: '',
  interval: 1,
  display: {
    short: true, // display short format 00:00:00.000
    days: false, // Won't be displayed on short format
    hours: true,
    minutes: true,
    seconds: true,
    milliseconds: true
  },
};

export default translate('Countdown')(Countdown);
